import { useEffect, useMemo, useRef, useState, useCallback, useSyncExternalStore } from 'react';
import { LiveClient } from './LiveClient';
import { isEqual } from 'lodash-es';
import { checkNever } from '@towns-protocol/sdk';
const getOptionsThatShouldCauseRestartWhenChanged = (options) => options
    ? [
        options.startClosed,
        options.minUptime,
        options.maxRetries,
        options.connectionTimeout,
        options.maxEnqueuedMessages,
        options.maxReconnectionDelay,
        options.minReconnectionDelay,
        options.reconnectionDelayGrowFactor,
        options.debug,
    ]
    : [];
const getWsMemoKey = (options) => {
    return JSON.stringify([
        options.liveWorkerUrl,
        options.wallet,
        options.deviceId,
        ...getOptionsThatShouldCauseRestartWhenChanged(options.wsOptions),
    ]);
};
/**
 * React hook for managing a LiveClient connection
 * Modeled after PartyKit's usePartyConnection hook
 */
export function useLiveClient(options) {
    const [client, setClient] = useState();
    const [state, setState] = useState({
        ready: false,
        connectionStatus: 'disconnected',
        deviceState: undefined,
        devices: [],
        spaces: {},
        lastError: undefined,
        isReconnecting: false,
    });
    // Use refs to maintain stable references to event handlers
    const onReadyRef = useRef(options.onReady);
    const onOpenRef = useRef(options.onOpen);
    const onCloseRef = useRef(options.onClose);
    const onErrorRef = useRef(options.onError);
    const onMessageRef = useRef(options.onMessage);
    // Update refs when handlers change
    useEffect(() => {
        onReadyRef.current = options.onReady;
        onOpenRef.current = options.onOpen;
        onCloseRef.current = options.onClose;
        onErrorRef.current = options.onError;
        onMessageRef.current = options.onMessage;
    }, [options.onReady, options.onOpen, options.onClose, options.onError, options.onMessage]);
    // Track cleanup functions
    const cleanupRef = useRef({});
    // Cleanup effect when client changes
    useEffect(() => {
        if (!client)
            return;
        // Subscribe to state changes
        const unsubState = client.state$.subscribe((newState, prevState) => {
            setState(newState);
            if (newState.ready && !prevState?.ready) {
                onReadyRef.current?.();
            }
            // Update ready state based on connection status
            switch (newState.connectionStatus) {
                case 'connecting':
                    break;
                case 'connected':
                    if (prevState?.connectionStatus !== 'connected') {
                        onOpenRef.current?.();
                    }
                    break;
                case 'disconnected':
                    if (prevState?.connectionStatus !== 'disconnected') {
                        console.log('[useLiveClient ws>] WebSocket closed, was', prevState?.connectionStatus);
                        onCloseRef.current?.({});
                    }
                    break;
                case 'error':
                    if (prevState?.connectionStatus !== 'error') {
                        onErrorRef.current?.({});
                    }
                    break;
                default:
                    checkNever(newState.connectionStatus);
                    break;
            }
        });
        // Set up message handler
        const unsubscribeMessage = client.onMessage((msg) => {
            onMessageRef.current?.(msg);
        });
        cleanupRef.current = { unsubscribe: unsubState, unsubscribeMessage };
        return () => {
            unsubState();
            unsubscribeMessage();
        };
    }, [client]);
    const wsMemoKeyRef = useRef(getWsMemoKey(options));
    const wsMemoKey = useMemo(() => {
        const newKey = getWsMemoKey(options);
        if (newKey !== wsMemoKeyRef.current) {
            console.log('[useLiveClient ws>] wsMemoKey changed:', newKey);
            wsMemoKeyRef.current = newKey;
        }
        return wsMemoKeyRef.current;
    }, [options]);
    // Create LiveClient instance
    const clientOptions = useMemo(() => {
        if (!options.wallet || !options.spaceId || !options.liveWorkerUrl || !options.deviceId) {
            console.log('[useLiveClient ws>] Cannot create client: missing required options', options);
            return undefined;
        }
        console.log('[useLiveClient ws>] Creating client with options:', options);
        return {
            liveWorkerUrl: options.liveWorkerUrl,
            wallet: options.wallet,
            deviceId: options.deviceId,
            spaceId: options.spaceId,
            wsOptions: options.wsOptions,
            domain: options.domain,
            audioManagerFactory: options.audioManagerFactory,
        };
    }, [wsMemoKey, options.spaceId]);
    // Connect function
    const connect = useCallback(async () => {
        if (!clientOptions) {
            console.error('[useLiveClient ws>] Cannot connect: wallet is required');
            return;
        }
        if (client && isEqual(client.options, clientOptions)) {
            console.log('[useLiveClient ws>] Already have a client, just connect it');
            // Already have a client, just connect it
            await client.connect({ maxRetryCount: 100 });
            return;
        }
        console.log('[useLiveClient ws>] Disconnecting old client');
        client?.disconnect();
        const newClient = new LiveClient(clientOptions);
        console.log('[useLiveClient ws>] Creating new client');
        setClient(newClient);
        // Connect
        try {
            await newClient.connect({ maxRetryCount: 100 });
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to connect:', error);
            setClient(undefined);
            throw error;
        }
    }, [client, clientOptions]);
    // Disconnect function
    const disconnect = useCallback(() => {
        if (client) {
            client.disconnect();
            setClient(undefined);
        }
    }, [client]);
    // Subscribe function
    const updateSubscriptions = useCallback(async (spaceIds) => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot subscribe: not connected');
            return undefined;
        }
        try {
            return await client.updateSubscriptions(spaceIds);
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to subscribe:', error);
            return undefined;
        }
    }, [client]);
    // Join channel function
    const joinChannel = useCallback(async (spaceId, channelId) => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot join channel: not connected');
            return undefined;
        }
        try {
            return await client.joinChannel(spaceId, channelId);
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to join channel:', error instanceof Error ? error.message : String(error));
            return undefined;
        }
    }, [client]);
    // Leave channel function
    const leaveChannel = useCallback(async () => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot leave channel: not connected');
            return undefined;
        }
        try {
            return await client.leaveChannel();
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to leave channel:', error);
            return undefined;
        }
    }, [client]);
    // Leave stage function
    const leaveStage = useCallback(async () => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot leave stage: not connected');
            return undefined;
        }
        try {
            return await client.leaveStage();
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to leave stage:', error);
            return undefined;
        }
    }, [client]);
    // Join stage function
    const joinStage = useCallback(async () => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot join stage: not connected');
            return undefined;
        }
        try {
            return await client.joinStage();
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to join stage:', error);
            return undefined;
        }
    }, [client]);
    // Moderate stage function
    const moderateStage = useCallback(async (channelId, action) => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot moderate stage: not connected');
            return undefined;
        }
        try {
            return await client.moderateStage(channelId, action);
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to moderate stage:', error);
            return undefined;
        }
    }, [client]);
    // Ping function
    const ping = useCallback(async () => {
        if (!client) {
            console.error('[useLiveClient ws>] Cannot ping: not connected');
            return undefined;
        }
        try {
            return await client.ping();
        }
        catch (error) {
            console.error('[useLiveClient ws>] Failed to ping:', error);
            return undefined;
        }
    }, [client]);
    // Auto-connect on mount if requested
    useEffect(() => {
        if (clientOptions) {
            console.log('[useLiveClient ws>] Auto-connecting... with new client options:', clientOptions);
            void connect().catch((error) => {
                console.error('[useLiveClient ws>]  Auto-connect failed:', error);
            });
        }
    }, [clientOptions]); // Only depend on autoConnect flag
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (client) {
                client.disconnect();
            }
        };
    }, [client]);
    return {
        client,
        state,
        connect,
        disconnect,
        updateSubscriptions,
        joinChannel,
        leaveChannel,
        leaveStage,
        joinStage,
        moderateStage,
        ping,
    };
}
/**
 * Hook to observe a specific part of the LiveClient state
 * Similar to useObservableWith from the codebase
 */
export function useLiveClientState(client, selector) {
    const observable = useMemo(() => client?.state$.map(selector), [client, selector]);
    const subscribeFn = useCallback((subFn) => {
        return observable?.subscribe(subFn) ?? (() => { });
    }, [observable]);
    const getSnapshotFn = useCallback(() => {
        return observable?.value;
    }, [observable]);
    return useSyncExternalStore(subscribeFn, getSnapshotFn);
}
/**
 * Hook to get live state for a specific space
 */
export function useLiveSpaceState(client, spaceId) {
    return useLiveClientState(client, useCallback((state) => state.spaces[spaceId]?.state, [spaceId]));
}
/**
 * Hook to get the live channels
 */
export function useLiveSpaceSummary(client, spaceId) {
    return useLiveClientState(client, useCallback((state) => state.spaces[spaceId], [spaceId]));
}
/**
 * Hook to get live state for a specific channel
 */
export function useLiveChannelState(client, spaceId, channelId) {
    return useLiveClientState(client, useCallback((state) => {
        const summary = spaceId ? state.spaces[spaceId] : undefined;
        return channelId ? summary?.channels[channelId] : undefined;
    }, [spaceId, channelId]));
}
/**
 * Hook to get the current user's connection state
 */
export function useLiveUserDeviceState(client) {
    return useLiveClientState(client, useCallback((state) => state.deviceState, []));
}
/**
 * Hook to get the current user's devices connection state
 */
export function useLiveUserDevicesState(client) {
    return useLiveClientState(client, useCallback((state, _prevState, prevResult) => {
        if (prevResult?.deviceState !== state.deviceState ||
            prevResult?.devices !== state.devices) {
            return {
                devices: state.devices,
                deviceState: state.deviceState,
            };
        }
        return prevResult;
    }, []));
}
/**
 * Hook to get the last error from the LiveClient
 * Returns the error message string or undefined if no error
 */
export function useLiveClientError(client) {
    return useLiveClientState(client, useCallback((state) => state.lastError, []));
}
// ============================================================================
// Audio Hooks
// ============================================================================
export const useLiveAudioState = (client) => {
    return useLiveClientState(client, useCallback((state) => state.audioState, []));
};
export const useLiveAudioManager = (client) => {
    return useMemo(() => client?.audioManager, [client?.audioManager]);
};
//# sourceMappingURL=useLiveClient.js.map